import React, { useMemo, useState } from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import { GRID_TOKENS as tokens } from "../tokens";
import {
  dayKeyOf,
  dowLetter,
  GridWindow,
  longDayLabel,
  hourMarks,
  layoutWeekColumn,
  MobileAppt,
  snapPressMinute,
} from "./geometry";
import { MobileBlock } from "./MobileBlock";
import { MobileSlotMenu, slotMenuPlacement } from "./MobileSlotMenu";
import styles from "./MobileGrid.module.css";

export interface MobileWeekDay {
  date: Date;
  closed: boolean;
}

/** An empty-slot press in week view: the column's day + snapped minute. */
export interface WeekSlotPress {
  dayKey: string;
  startMin: number;
}

interface WeekSlotMenuState extends WeekSlotPress {
  left: number;
  top: number;
}

export interface MobileWeekGridProps {
  days: MobileWeekDay[];
  /** The provider's appointments + time blocks for the week. */
  appts: MobileAppt[];
  window: GridWindow;
  selectedKey: string;
  todayKey: string;
  /** Current minute (drawn in today's column when it is in the week). */
  nowMin: number;
  onBlockTap: (appt: MobileAppt) => void;
  /** Day header / `+N` chip: open that day in day view. */
  onOpenDay: (date: Date) => void;
  /** Slot menu (same as day view); omitted = presses do nothing. */
  onSlotAppointment?: (slot: WeekSlotPress) => void;
  onSlotTimeBlock?: (slot: WeekSlotPress) => void;
}

/**
 * Week grid: one provider across 7 equal columns, sticky hour rail + day
 * header, at most two side-by-side blocks with the rest collapsed into a
 * `+N` chip (tapping it opens that day in day view, where every block shows).
 */
export const MobileWeekGrid: React.FC<MobileWeekGridProps> = ({
  days,
  appts,
  window,
  selectedKey,
  todayKey,
  nowMin,
  onBlockTap,
  onOpenDay,
  onSlotAppointment,
  onSlotTimeBlock,
}) => {
  const ppm = tokens.ppmWeek;
  const [slotMenu, setSlotMenu] = useState<WeekSlotMenuState | null>(null);
  const laneH = (window.endMin - window.startMin) * ppm;
  const marks = useMemo(() => hourMarks(window, ppm), [window, ppm]);

  const columns = useMemo(
    () =>
      days.map((d) => {
        const key = dayKeyOf(d.date);
        return {
          ...d,
          key,
          ...layoutWeekColumn(
            appts.filter((a) => a.dayKey === key),
            window,
            ppm,
          ),
        };
      }),
    [days, appts, window, ppm],
  );

  // Empty-slot press: the day view's Appointment / Time block menu, for the
  // week's provider on the pressed column's day. A closed day's hatch is
  // visual only (like the day view): it still takes a press.
  const pressable = !!onSlotAppointment && !!onSlotTimeBlock;
  const handleLanePress = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pressable) return;
    const target = e.target as HTMLElement;
    if (target.closest("[data-block]") || target.closest("button")) return;
    if (slotMenu) {
      setSlotMenu(null);
      return;
    }
    const colEl = target.closest("[data-day]");
    const dayKey = colEl?.getAttribute("data-day");
    if (!dayKey) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const startMin = snapPressMinute(e.clientY - rect.top, window, ppm);
    setSlotMenu({
      dayKey,
      startMin,
      ...slotMenuPlacement(startMin, e.clientX, rect, window, ppm),
    });
  };

  const pick = (fn?: (slot: WeekSlotPress) => void) => () => {
    if (!slotMenu || !fn) return;
    const { dayKey, startMin } = slotMenu;
    setSlotMenu(null);
    fn({ dayKey, startMin });
  };

  return (
    <Box className={styles.inner} data-testid="mobile-week-grid">
      <Box className={styles.headRow}>
        <Box className={styles.headSpacer} />
        {columns.map((c) => {
          const selected = c.key === selectedKey;
          return (
            <UnstyledButton
              key={c.key}
              className={styles.dayHead}
              data-closed={c.closed}
              onClick={() => onOpenDay(c.date)}
              aria-label={longDayLabel(c.date)}
            >
              <Text
                span
                className={styles.dow}
                data-closed={c.closed}
                data-selected={selected}
              >
                {dowLetter(c.date)}
              </Text>
              <Text
                span
                className={styles.dayNum}
                data-closed={c.closed}
                data-today={c.key === todayKey}
                data-selected={selected}
              >
                {c.date.getDate()}
              </Text>
            </UnstyledButton>
          );
        })}
      </Box>
      <Box className={styles.gridRow}>
        <Box className={styles.rail} style={{ height: laneH }}>
          {marks.map((m) => (
            <Text
              span
              key={m.minute}
              className={styles.hourLabel}
              style={{ top: m.labelTop, fontSize: tokens.hourLabelPx }}
            >
              {m.label}
            </Text>
          ))}
        </Box>
        <Box
          className={styles.lane}
          style={{ height: laneH, cursor: pressable ? "copy" : undefined }}
          onClick={handleLanePress}
          data-testid="mobile-week-lane"
        >
          {marks.map((m) => (
            <Box
              key={m.minute}
              className={styles.hourLine}
              data-edge={m.edge}
              style={{ top: m.top }}
            />
          ))}
          {columns.map((c) => (
            <Box
              key={c.key}
              className={styles.col}
              data-closed={c.closed}
              data-day={c.key}
              data-testid={`mobile-week-col-${c.key}`}
              style={{ flex: "1 1 0%", height: laneH }}
            >
              {c.blocks.map((p) => (
                <MobileBlock
                  key={p.appt.id}
                  placed={p}
                  variant="week"
                  ppm={ppm}
                  onTap={() => onBlockTap(p.appt)}
                />
              ))}
              {c.more.map((m) => (
                <UnstyledButton
                  key={m.startMin}
                  className={styles.moreChip}
                  style={{ top: m.top }}
                  onClick={() => onOpenDay(c.date)}
                  aria-label={`+${m.count} · ${longDayLabel(c.date)}`}
                >
                  +{m.count}
                </UnstyledButton>
              ))}
              {c.key === todayKey &&
                nowMin >= window.startMin &&
                nowMin <= window.endMin && (
                  <Box
                    className={styles.nowLine}
                    data-testid="mobile-now-line"
                    style={{
                      left: 0,
                      right: 0,
                      top: (nowMin - window.startMin) * ppm,
                    }}
                  />
                )}
            </Box>
          ))}
          {slotMenu && (
            <MobileSlotMenu
              left={slotMenu.left}
              top={slotMenu.top}
              onAppointment={pick(onSlotAppointment)}
              onTimeBlock={pick(onSlotTimeBlock)}
              onClose={() => setSlotMenu(null)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
